'use client';
import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

interface City {
  name: string;
  image: string;
  url: string;
}

const CITY_ORDER = ['dubai', 'ras-al-khaimah', 'ajman', 'sharjah', 'abu-dhabi'];

export default function CitiesSection() {
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const res = await fetch('/api/taxonomies/locations');
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) {
            // Filter and sort according to CITY_ORDER
            const filteredAndOrdered = CITY_ORDER.map((slug) => {
              const loc = data.find((item) => item.slug === slug);
              if (loc) {
                // If it is Ras Al Khaimah, add a newline in name for formatting
                const formattedName =
                  loc.slug === 'ras-al-khaimah' ? 'RAS AL\nKHAIMAH' : loc.name.toUpperCase();
                return {
                  name: formattedName,
                  image: loc.banner_image || '/uploads/placeholder.jpg',
                  url: `/${loc.slug}`,
                };
              }
              return null;
            }).filter((item) => item !== null) as City[];

            setCities(filteredAndOrdered);
          }
        }
      } catch (err) {
        console.error('Failed to fetch cities taxonomy:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCities();
  }, []);

  return (
    <section className="bg-[#f8faf9] py-16">
      <div className="container mx-auto px-6" style={{ maxWidth: '1480px' }}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Left: Title column */}
          <div className="lg:col-span-3">
            <h2
              className="mb-4"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '30px',
                fontWeight: 300,
                color: 'rgb(80, 81, 79)',
                lineHeight: '38px',
              }}
            >
              Explore Cities
            </h2>
            <p
              className="max-w-[280px]"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '15px',
                fontWeight: 400,
                color: 'rgb(120, 120, 120)',
                lineHeight: '24px',
              }}
            >
              Explore Cities: From Dubai to Abu Dhabi, discover the finest plots in UAE&apos;s most
              dynamic urban destinations
            </p>
          </div>

          {/* Right: Cities Cards */}
          <div className="lg:col-span-9">
            {loading ? (
              <div className="h-[460px] flex flex-col items-center justify-center space-y-4">
                <Loader2 size={40} className="animate-spin text-[#017743]" />
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
                  Loading Cities...
                </p>
              </div>
            ) : cities.length === 0 ? (
              <div className="h-[460px] flex items-center justify-center text-slate-400">
                <p className="text-sm font-semibold uppercase tracking-wider">No cities loaded</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {cities.map((city, index) => (
                  <a
                    key={index}
                    href={city.url}
                    className="relative h-[460px] block overflow-hidden group shadow-lg hover:-translate-y-2 transition-all duration-300 rounded-none cursor-pointer"
                    style={{
                      boxShadow:
                        '0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.08)',
                    }}
                  >
                    <img
                      src={city.image}
                      alt={city.name}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div
                      className="absolute inset-0"
                      style={{
                        background:
                          'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.05) 50%, rgba(0,0,0,0.5) 100%)',
                      }}
                    />

                    <div className="absolute top-0 left-0 right-0 p-6 z-10">
                      <h3
                        className="text-white whitespace-pre-line"
                        style={{
                          fontFamily: 'Montserrat, sans-serif',
                          fontSize: '18px',
                          fontWeight: 600,
                          lineHeight: '24px',
                          letterSpacing: '0.05em',
                        }}
                      >
                        {city.name}
                      </h3>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
